import { useState, useEffect } from 'react';
import './Drivers.css';
import { toast } from 'react-toastify';
import { driverAPI } from '../../../config/driver-api';
import { X, Trash2, Pencil, MapPin, Eye } from 'lucide-react';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';
import DriverMapView from './DriverMapView';

const Drivers = ({ adminData, onPageChange, type = 'delivery' }) => {
  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_STAFF);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_STAFF);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_STAFF);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driverLoading, setDriverLoading] = useState(false);
  const [driverError, setDriverError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [isLocationFetching, setIsLocationFetching] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    color: '#FE8403',
    status: true,
    profilePicture: '',
    profilePictureFile: null,
    earningType: type === 'packing' ? '' : 'salary_plus_overtime',
    commission: 0,
    overtime: 0
  });

  const fetchDrivers = async () => {
    try {
      setDriverLoading(true);
      setDriverError(null);

      const response = await driverAPI.getAllDrivers(type);

      if (response.success) {
        setDrivers(response.data);
      } else {
        setDriverError(response.error || 'Failed to fetch drivers');
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setDriverError('Error fetching drivers: ' + error.message);
    } finally {
      setDriverLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [type]);

  const revokePreviewUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => {
      revokePreviewUrl(prev.profilePicture);
      return {
        ...prev,
        profilePicture: previewUrl,
        profilePictureFile: file
      };
    });
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Phone is optional
    const cleanPhone = phone.replace(/[\s\-]/g, '');
    return /^\+974\d{8}$/.test(cleanPhone) || /^\d{8}$/.test(cleanPhone);
  };

  const formatPhoneWithCountryCode = (phone) => {
    if (!phone) return '';
    const cleanPhone = phone.replace(/[\s\-]/g, '');
    // Already has +974 prefix
    if (cleanPhone.startsWith('+974')) {
      return cleanPhone;
    }
    // Just 8 digits - add prefix
    if (/^\d{8}$/.test(cleanPhone)) {
      return `+974${cleanPhone}`;
    }
    return cleanPhone;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        toast.error('Name is required');
        setLoading(false);
        return;
      }

      const phone = formData.phoneNumber.trim();
      if (phone && !validatePhoneNumber(phone)) {
        toast.error('Phone number must be 8 digits');
        setLoading(false);
        return;
      }

      const isPacking = type === 'packing';
      const needsCommission = !isPacking && ['salary_plus_commission_plus_overtime', 'commission_plus_overtime'].includes(formData.earningType);
      const needsOvertime = !isPacking && ['salary_plus_overtime', 'salary_plus_commission_plus_overtime', 'commission_plus_overtime'].includes(formData.earningType);

      if (needsCommission) {
        if (formData.commission === '' || formData.commission === null) {
          toast.error('Commission is required');
          setLoading(false);
          return;
        }

        if (Number(formData.commission) <= 0) {
          toast.error('Commission must be greater than 0');
          setLoading(false);
          return;
        }
      }

      if (needsOvertime) {
        if (formData.overtime === '' || formData.overtime === null) {
          toast.error('Overtime amount is required');
          setLoading(false);
          return;
        }

        if (Number(formData.overtime) <= 0) {
          toast.error('Overtime must be greater than 0');
          setLoading(false);
          return;
        }
      }

      const payload = {
        name: formData.name.trim(),
        phoneNumber: formatPhoneWithCountryCode(phone),
        color: formData.color || "#FE8403",
        status: formData.status,
        profilePictureFile: formData.profilePictureFile,
        earningType: isPacking ? '' : formData.earningType,
        commission: isPacking ? 0 : (needsCommission ? Number(formData.commission) || 0 : 0),
        overtime: isPacking ? 0 : (needsOvertime ? Number(formData.overtime) || 0 : 0),
        type: type
      };

      let result;
      if (isEditMode && editingDriverId) {
        result = await driverAPI.updateDriver(editingDriverId, payload);
      } else {
        result = await driverAPI.addDriver(payload);
      }

      if (result.success) {
        toast.success(isEditMode ? 'Driver updated successfully!' : 'Driver added successfully!');
        handleCloseDialog();
        fetchDrivers();
      } else {
        toast.error(result.error || (isEditMode ? 'Failed to update driver' : 'Failed to add driver'));
      }
    } catch (error) {
      console.error(isEditMode ? 'Error updating driver:' : 'Error adding driver:', error);
      toast.error(isEditMode ? 'An error occurred while updating the driver' : 'An error occurred while adding the driver');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    revokePreviewUrl(formData.profilePicture);
    setIsDialogOpen(false);
    setIsEditMode(false);
    setEditingDriverId(null);
    setFormData({
      name: '',
      phoneNumber: '',
      color: '#FE8403',
      status: true,
      profilePicture: '',
      profilePictureFile: null,
      earningType: type === 'packing' ? '' : 'salary_plus_overtime',
      commission: 0,
      overtime: 0
    });
  };

  const handleEditDriver = (driver) => {
    revokePreviewUrl(formData.profilePicture);
    setFormData({
      name: driver.name || '',
      phoneNumber: driver.phoneNumber || '',
      color: driver.color || '#FE8403',
      status: driver.status !== false,
      profilePicture: driver.profilePicture || '',
      profilePictureFile: null,
      earningType: driver.earningType || (type === 'packing' ? '' : 'salary_plus_overtime'),
      commission: driver.commission || 0,
      overtime: driver.overtime || 0
    });
    setIsEditMode(true);
    setEditingDriverId(driver._id);
    setIsDialogOpen(true);
  };

  const handleDeleteDriver = (driverId) => {
    setDriverToDelete(driverId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!driverToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await driverAPI.deleteDriver(driverToDelete);

      if (response.success) {
        toast.success('Driver deleted successfully!');
        fetchDrivers();
        setShowDeleteDialog(false);
        setDriverToDelete(null);
      } else {
        toast.error(response.error || 'Failed to delete driver');
      }
    } catch (error) {
      console.error('Error deleting driver:', error);
      toast.error('Failed to delete driver');
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDriverToDelete(null);
  };

  const handleMapView = async () => {
    try {
      setIsLocationFetching(true);

      // Request location from all drivers
      await driverAPI.requestLocation();

      // Wait for 5 seconds
      await new Promise(resolve => setTimeout(resolve, 5000));

      // Fetch drivers again to get updated locations
      await fetchDrivers();

      setIsLocationFetching(false);
      setShowMapView(true);
    } catch (error) {
      console.error('Error in handleMapView:', error);
      toast.error('Failed to fetch updated locations');
      setIsLocationFetching(false);
      setShowMapView(true);
    }
  };

  return (
    <div className="driver-page">
      <div className="driver-page-header">
        <div className="driver-page-title">
          <h1>{type === 'packing' ? 'Packing Staff Management' : 'Delivery Staff Management'}</h1>
          <p>View and manage all {type === 'packing' ? 'packing staff' : 'delivery staff'} in the system</p>
        </div>
        <div className="driver-header-actions">
          {canCreate && (
            <button className="driver-create-btn" onClick={() => setIsDialogOpen(true)}>
              {type === 'packing' ? 'Add Packing Staff' : 'Add Delivery Staff'}
            </button>
          )}
          {type !== 'packing' && (
            <button
              className="driver-map-view-btn"
              onClick={handleMapView}
            >
              <MapPin size={16} />
              Map View
            </button>
          )}
        </div>
      </div>

      <div className="driver-table-container">
        {driverLoading ? (
          <div className="driver-loading-state">
            <div className="driver-loading-spinner"></div>
            <p>Loading {type === 'packing' ? 'packing staff' : 'delivery staff'}...</p>
          </div>
        ) : driverError ? (
          <div className="driver-error-state">
            <p>{driverError}</p>
            <button
              onClick={() => fetchDrivers()}
              className="driver-retry-button"
            >
              Retry
            </button>
          </div>
        ) : drivers.length === 0 ? (
          <div className="driver-empty-state">
            <p>No {type === 'packing' ? 'packing staff' : 'delivery staff'} found</p>
          </div>
        ) : (
          <>
            <div className="driver-table-wrapper">
              <table className="driver-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Color</th>
                    {type !== 'packing' && <th>Earning Type</th>}
                    <th>Created At</th>
                    <th>Last Online</th>
                    <th>Status</th>
                    {(canEdit || canDelete) && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((driver) => (
                    <tr key={driver._id}>
                      <td>
                        {driver.profilePicture ? (
                          <img
                            src={driver.profilePicture}
                            alt={driver.name}
                            className="driver-avatar"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://ablemart-media.s3.me-central-1.amazonaws.com/general/default_staff.png';
                            }}
                          />
                        ) : (
                          <div className="driver-avatar driver-avatar-placeholder">
                            {driver.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td className="driver-name-cell">
                        <span className="driver-name-text">{driver.name}</span>
                      </td>
                      <td>{driver.phoneNumber || '-'}</td>
                      <td>
                        <div className="driver-color-cell">
                          <span
                            className="driver-color-swatch"
                            style={{ backgroundColor: driver.color || '#FE8403' }}
                          ></span>
                          <span className="driver-color-code">{driver.color || '#FE8403'}</span>
                        </div>
                      </td>
                      {type !== 'packing' && (
                        <td>
                          {(() => {
                            const typeVal = driver.earningType === 'fixed'
                              ? 'salary_plus_overtime'
                              : driver.earningType === 'commission'
                                ? 'commission_plus_overtime'
                                : (driver.earningType || 'salary_plus_overtime');

                            let label = '';
                            let details = '';

                            if (typeVal === 'salary_plus_overtime') {
                              label = 'Salary + OT';
                              if (driver.overtime) {
                                details = ` (OT: ${driver.overtime})`;
                              }
                            } else if (typeVal === 'salary_plus_commission_plus_overtime') {
                              label = 'Salary + Comm + OT';
                              const comm = driver.commission || 0;
                              const ot = driver.overtime || 0;
                              details = ` (C: ${comm}, OT: ${ot})`;
                            } else if (typeVal === 'commission_plus_overtime') {
                              label = 'Comm + OT';
                              const comm = driver.commission || 0;
                              const ot = driver.overtime || 0;
                              details = ` (C: ${comm}, OT: ${ot})`;
                            }

                            return (
                              <span className={`earning-type-badge ${typeVal}`}>
                                {label}{details}
                              </span>
                            );
                          })()}
                        </td>
                      )}
                      <td>{driver.createdAt || '-'}</td>
                      <td className="driver-nowrap-cell">
                        {helpers.formatQatarTime(driver.lastOnline)}
                      </td>
                      <td>
                        <span className={`driver-status-badge ${driver.status !== false ? 'active' : 'inactive'}`}>
                          {driver.status !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="driver-action-buttons">
                          {type !== 'packing' && (
                            <button
                              className="driver-view-orders-btn"
                              onClick={() => onPageChange('orders', false, { driverId: driver._id, driverName: driver.name })}
                              title="View orders"
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className="driver-edit-btn"
                              onClick={() => handleEditDriver(driver)}
                              title={type === 'packing' ? 'Edit packing staff' : 'Edit delivery staff'}
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="driver-delete-btn"
                              onClick={() => handleDeleteDriver(driver._id)}
                              title={type === 'packing' ? 'Delete packing staff' : 'Delete delivery staff'}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {showMapView && (
        <DriverMapView drivers={drivers} onClose={() => setShowMapView(false)} />
      )}

      {isDialogOpen && (
        <div className="driver-dialog-overlay">
          <div className="driver-dialog-box">
            <div className="driver-dialog-header">
              <div>
                <h2>{isEditMode ? (type === 'packing' ? 'Edit Packing Staff' : 'Edit Delivery Staff') : (type === 'packing' ? 'Add Packing Staff' : 'Add Delivery Staff')}</h2>
              </div>
              <button className="driver-close-btn" onClick={handleCloseDialog}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="driver-form">
              {isEditMode && (
                <div className="driver-profile-section">
                  <div className="driver-profile-upload-wrapper">
                    <div className="driver-profile-upload-container">
                      <div className="driver-profile-preview-container">
                        {formData.profilePicture ? (
                          <img
                            src={formData.profilePicture}
                            alt={formData.name || 'Driver'}
                            className="driver-profile-upload-preview"
                          />
                        ) : (
                          <div className="driver-profile-upload-preview driver-profile-upload-placeholder">
                            {formData.name?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <label htmlFor="profilePicture" className="driver-profile-edit-badge" title="Upload Photo">
                        <Pencil size={14} />
                        <input
                          type="file"
                          id="profilePicture"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="driver-form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={type === 'packing' ? 'Enter packing staff name' : 'Enter delivery staff name'}
                />
              </div>

              <div className="driver-form-group">
                <label htmlFor="phoneNumber">Phone number <span className="driver-optional">(Optional)</span></label>
                <div className="driver-phone-input">
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter 8-digit phone number"
                  />
                </div>
              </div>

              <div className="driver-form-group">
                <label htmlFor="color">Color</label>
                <div className="driver-color-input-wrapper">
                  <input
                    type="color"
                    id="colorPicker"
                    value={formData.color || '#FE8403'}
                    onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                    className="driver-color-picker"
                  />
                  <input
                    type="text"
                    id="color"
                    name="color"
                    value={formData.color || ''}
                    onChange={handleInputChange}
                    placeholder="#FE8403"
                    className="driver-color-text-input"
                  />
                </div>
              </div>

              <div className="driver-form-group">
                <label>Status</label>
                <label className="driver-status-switch">
                  <input
                    type="checkbox"
                    checked={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.checked }))}
                  />
                  <span className="driver-status-slider"></span>
                </label>
              </div>

              {type !== 'packing' && (
                <>
                  <div className="driver-form-group">
                    <label htmlFor="earningType">Earning Type</label>
                    <select
                      id="earningType"
                      name="earningType"
                      value={formData.earningType}
                      onChange={handleInputChange}
                      className="driver-select-input"
                    >
                      <option value="salary_plus_overtime">Full Time Salary + Over time</option>
                      <option value="salary_plus_commission_plus_overtime">Salary + Commission + Over time</option>
                      <option value="commission_plus_overtime">Commission + Over time</option>
                    </select>
                  </div>

                  <div className="driver-form-row">
                    {['salary_plus_commission_plus_overtime', 'commission_plus_overtime'].includes(formData.earningType) && (
                      <div className="driver-form-group">
                        <label htmlFor="commission">Commission Amount (QR)</label>
                        <input
                          type="number"
                          id="commission"
                          name="commission"
                          value={formData.commission}
                          onChange={handleInputChange}
                          placeholder="Enter commission amount"
                        />
                      </div>
                    )}

                    {['salary_plus_overtime', 'salary_plus_commission_plus_overtime', 'commission_plus_overtime'].includes(formData.earningType) && (
                      <div className="driver-form-group">
                        <label htmlFor="overtime">Overtime Amount (QR)</label>
                        <input
                          type="number"
                          id="overtime"
                          name="overtime"
                          value={formData.overtime}
                          onChange={handleInputChange}
                          placeholder="Enter overtime amount"
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="driver-form-actions">
                <button
                  type="button"
                  className="driver-cancel-btn"
                  onClick={handleCloseDialog}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="driver-submit-btn"
                  disabled={loading}
                >
                  {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? (type === 'packing' ? 'Update Packing Staff' : 'Update Delivery Staff') : (type === 'packing' ? 'Add Packing Staff' : 'Add Delivery Staff'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteDialog && (
        <div className="driver-dialog-overlay">
          <div className="driver-dialog-box driver-delete-dialog">
            <div className="driver-dialog-header">
              <h2>Confirm Delete</h2>
              <button className="driver-close-btn" onClick={cancelDelete}>
                <X size={20} />
              </button>
            </div>

            <div className="driver-delete-content">
              <p>
                Are you sure you want to delete this {type === 'packing' ? 'packing staff' : 'driver'}?
              </p>
            </div>

            <div className="driver-form-actions">
              <button
                className="driver-cancel-btn"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="driver-delete-confirm-btn"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLocationFetching && (
        <div className="driver-dialog-overlay">
          <div className="driver-dialog-box fetching-location-dialog">
            <div className="fetching-location-content">
              <div className="driver-loading-spinner location-spinner"></div>
              <p>Fetching current location of all staffs...</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;
